/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Button, Skeleton, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon, ArrowPreviousIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CareerAcademyModuleFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import Section from 'bundles/front-page/components/Section';
import useLohpPathwayCareerRolesData from 'bundles/front-page/redesign-modules/career-academy/hooks/useLohpPathwayCareerRolesData';
import CompactCareerRoleCard from 'bundles/unified-common/components/career-academy/CompactCareerRoleCard';

import _t from 'i18n!nls/front-page';

type Props = {
  content?: CareerAcademyModuleFragment;
};

const styles = {
  section: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-400);
  `,

  sectionHeader: css`
    display: inline-flex;
    align-items: center;

    ${breakpoints.down('xs')} {
      justify-content: space-between;
    }
  `,
  sectionTitle: css`
    display: inline-flex;
    ${typography2.subtitleLarge};

    ${breakpoints.down('xs')} {
      ${typography2.subtitleMedium};
    }
  `,
  ctaIconButton: css`
    display: inline-flex;
    align-self: flex-end;
    padding: 0;
  `,
  ctaText: css`
    display: inline-flex;
    align-items: center;
  `,
  cta: css`
    margin-left: var(--cds-spacing-200);
  `,
  container: css`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;
    min-width: unset;
    margin-top: calc(var(--cds-spacing-50) * -1);

    ${breakpoints.down('md')} {
      grid-template-columns: repeat(4, 1fr);
    }

    ${breakpoints.down('sm')} {
      grid-template-columns: repeat(3, 1fr);
    }
  `,
  ssrContainer: css`
    /*
    * This is a hacky way of hiding trailing placeholder cards,
    * since media queries are not available for SSR and we need to
    * display the correct number of cards based on the screen size.
    */
    ${breakpoints.down('md')} {
      > div:nth-child(n + 5) {
        display: none;
      }
    }

    ${breakpoints.down('sm')} {
      > div:nth-child(n + 4) {
        display: none;
      }
    }
  `,
};

const CareerAcademyModuleRedesignModuleRenderer = ({ content }: Props) => {
  const { title: moduleTitle, ctaLabel } = content ?? {};
  const track = useTracker();
  const [isClient, setIsClient] = React.useState(false);
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const isMobileOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('md'));

  const isRTL = isRightToLeft(_t.getLocale());
  const ctaText = ctaLabel ?? _t('Explore all');
  const ctaTarget = '/career-academy';
  const handleBtnClick = () => {
    track('click_button', {
      button: {
        name: 'career_academy_cta',
        linkName: ctaText,
        linkType: 'internal',
        linkURL: ctaTarget,
      },
    });
  };

  const { data: careerRoles, loading, error } = useLohpPathwayCareerRolesData();

  const heading = moduleTitle ?? _t('Explore careers');
  const visibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: {
        moduleType: 'collections_module',
        heading,
      },
    }
  );

  React.useEffect(() => {
    // this will set true only if the component is mounted on client side
    // it will be false during SSR and rehydration
    setIsClient(true);
  }, []);

  // During SSR, default to desktop layout (5 cards) to ensure placeholders render
  let numberOfCardsToRender = 5; // Default fallback
  if (isClient) {
    // Only use media queries after client-side hydration
    if (isTabletOrSmaller) numberOfCardsToRender = 4;
    if (isMobile || isMobileOrSmaller) numberOfCardsToRender = 3;
  }

  // Don't render anything if there's an error, but only after client-side hydration
  // During SSR, we want to show placeholders even if there's an error
  if (isClient && error) {
    return null;
  }

  const showLoadingState = !isClient || loading;

  return (
    <Section css={styles.section} ref={visibilityTrackerRef} dataTestId="career-academy">
      <div css={styles.sectionHeader}>
        {showLoadingState ? (
          <Skeleton data-testid="skeleton" width="35%" height={40} css={styles.sectionTitle} />
        ) : (
          <Typography2 variant="subtitleLarge" component="h2" css={styles.sectionTitle}>
            {heading}
          </Typography2>
        )}

        {showLoadingState ? (
          <Skeleton data-testid="skeleton" width="8%" height={40} css={styles.cta} />
        ) : (
          <Button
            variant="ghost"
            size="small"
            component="a"
            href={ctaTarget}
            css={isMobile ? styles.ctaIconButton : styles.cta}
            onClick={handleBtnClick}
          >
            {isMobile && isRTL && <ArrowPreviousIcon color="interactive" size="large" />}
            {isMobile && !isRTL && <ArrowNextIcon color="interactive" size="large" />}
            {!isMobile && (
              <span css={styles.ctaText}>
                {isRTL ? (
                  <React.Fragment>
                    <ArrowPreviousIcon color="interactive" size="small" /> {ctaText}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {ctaText} <ArrowNextIcon color="interactive" size="small" />
                  </React.Fragment>
                )}
              </span>
            )}
          </Button>
        )}
      </div>

      <div css={[styles.container, !isClient && styles.ssrContainer]}>
        {Array.from({ length: numberOfCardsToRender }, (_, i) => {
          const careerRole = careerRoles?.[i];

          // For SSR / while loading, display placeholder cards
          if (showLoadingState) {
            return (
              <CompactCareerRoleCard key={`loading-${i}`} id={`loading-${i}`} slug="loading" showLoadingState={true} />
            );
          }

          // For CSR / after loading, display actual data
          if (careerRole) {
            return <CompactCareerRoleCard key={careerRole.id} {...careerRole} showLoadingState={false} />;
          }

          // Don't render anything if no data available
          return null;
        })}
      </div>
    </Section>
  );
};

export default CareerAcademyModuleRedesignModuleRenderer;
